import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { 
  Save, Trash2, Plus, Edit, Search, AlertCircle, CheckCircle,
  BookOpen, Briefcase, GraduationCap, Layers, Code, Award, ChevronDown
} from "lucide-react";
import axios from "axios"; // For API calls

export default function TeamBio() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [activeSection, setActiveSection] = useState("profile");
  const [avatar, setAvatar] = useState(null); // Hold the selected avatar file

  const [validationModal, setValidationModal] = useState({
    open: false,
    message: "",
    section: null,
    onConfirm: null,
    onCancel: null
  });
  
  
  // Form hooks
  const profileForm = useForm();
  const experienceForm = useForm();
  const educationForm = useForm();
  const skillsForm = useForm();
  const projectsForm = useForm();
  const awardsForm = useForm();
  const isFetching = useRef(false);

  // Load all profiles
  useEffect(() => {
    const fetchProfiles = async () => {
      if (isFetching.current) return; // Prevent multiple calls

      try {
        isFetching.current = true;
        setIsLoading(true);
        const response = await axios.get("/api/bio/profiles");
        //console.log("response",response, "\ndata", response.data)
        setProfiles(response.data);
      } catch (error) {
        showAlert("error", "Failed to load profiles");
      } finally {
        setIsLoading(false);
        isFetching.current = false;
      }
    };

    fetchProfiles();
  }, []);

  // Load profile data when selected
  useEffect(() => {
    if (selectedProfile) {
      loadProfileData(selectedProfile);
    }
  }, [selectedProfile]);

  const loadProfileData = (profile) => {
    //console.log("profile", profile);
    //console.log("skills", profile.skills);

    // Reset forms with general profile data
    profileForm.reset(profile.profile || {});
    experienceForm.reset({ experience: profile.experiences || [] });
    educationForm.reset({ education: profile.academicRecord || [] });
    
    // Process skills to categorize them
    const categorizedSkills = {
        technicalSkills: [],
        expertiseAreas: [],
        professionalSkills: []
    };

    profile.skills.forEach(skill => {
        if (skill.category === "technicalSkills") {
            categorizedSkills.technicalSkills.push(skill);
        } else if (skill.category === "expertiseAreas") {
            categorizedSkills.expertiseAreas.push(skill);
        } else if (skill.category === "professionalSkills") {
            categorizedSkills.professionalSkills.push(skill);
        }
    });

    // Reset the skills form with categorized skills
    skillsForm.reset({
        technicalSkills: categorizedSkills.technicalSkills,
        expertiseAreas: categorizedSkills.expertiseAreas,
        professionalSkills: categorizedSkills.professionalSkills,
    });
    
    //console.log("Resetted skillsForm form with:", skillsForm.getValues());
    projectsForm.reset({ projects: profile.projects || [] });
    awardsForm.reset({ awards: profile.awards || [] });
    setIsCreatingNew(false);
};

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "", message: "" }), 5000);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredProfiles = profiles.filter(profile => 
    profile.profile?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  

const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (file) {
    setAvatar(file);
    // Optional: Just to preview the file if needed
    const reader = new FileReader();
    reader.onloadend = () => {
      //console.log("Preview (Base64):", reader.result);
    };
    reader.readAsDataURL(file);
  }
};

const handleCreateNew = () => {
  const emptyProfile = {
    title: "",
    bio: "",
    location: "",
    birthday: "",
    email: "",
    website: "",
    social: { twitter: "", github: "", linkedin: "" },
    stats: Array(4).fill({ value: "", label: "" })
  };

  loadProfileData({
    profile: emptyProfile,
    experience: [],
    education: [],
    skills: [],
    projects: [],
    awards: []
  });

  setIsCreatingNew(true);
};

const handleSaveProfile = async (data) => {
  try {
    let serverResponse;
    setIsLoading(true);

    // Required fields in the form
    if (!data.email || !data.title || !data.bio || !data.location) {
      showAlert("error", "Please fill out all required profile fields.");
      return;
    }

    const technicalSkills = skillsForm.getValues("technicalSkills") || [];
    const expertiseAreas = skillsForm.getValues("expertiseAreas") || [];
    const professionalSkills = skillsForm.getValues("professionalSkills") || [];
   
    const allSkills = [
      ...technicalSkills,
      ...expertiseAreas,
      ...professionalSkills
    ];


    // Required sections: must contain at least 1 item
    const requiredSections = [
      {
        name: "education",
        value: educationForm.getValues("education") || [],
        message: "Please add at least one education entry."
      },
      {
        name: "experience",
        value: experienceForm.getValues("experience") || [],
        message: "Please add at least one experience entry."
      },
      {
        name: "skills",
        value: allSkills,
        message: "Please add at least one skill in the relevant section."
      }
    ];

    // Optional sections that can be skipped with a confirmation
    const optionalSections = [
      {
        name: "projects",
        value: projectsForm.getValues("projects") || [],
        message: "Please add at least one project."
      },
      {
        name: "awards",
        value: awardsForm.getValues("awards") || [],
        message: "Please add at least one award or recognition."
      }
    ];

    // Validate required sections
    for (const section of requiredSections) {
      if (section.value.length === 0) {
        showAlert("error", section.message);
        setActiveSection(section.name);
        return;
      }
    }

    // Validate optional sections (sequentially with confirmation)
    for (const section of optionalSections) {
      if (section.value.length === 0) {
        const shouldContinue = await new Promise((resolve) => {
          setValidationModal({
            open: true,
            message: `You haven't added any ${section.name}. ${section.message}`,
            section: section.name,
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });

        if (!shouldContinue) {
          setActiveSection(section.name);
          return;
        }
      }
    }

    // Prepare FormData
    const formData = new FormData();
    formData.append("title", data.title || "");
    formData.append("bio", data.bio || "");
    formData.append("location", data.location || "");
    formData.append("birthday", data.birthday || "");
    formData.append("email", data.email || "");
    formData.append("website", data.website || "");
    formData.append("twitter", data.social?.twitter || "");
    formData.append("github", data.social?.github || "");
    formData.append("linkedin", data.social?.linkedin || "");

    if (avatar) {
      formData.append("avatar", avatar);
    }

    // Normalize structure
    const normalizedTechnicalSkills = technicalSkills.map(skill => ({
      id: skill.id || Date.now().toString() + Math.random(), // fallback ID if needed
      category: "technicalSkills",
      name: skill.name,
      level: skill.level
    }));

    const normalizedExpertiseAreas = expertiseAreas.map(area => ({
      id: area.id || Date.now().toString() + Math.random(),
      category: "expertiseAreas",
      name: area.name,
      level: area.level
    }));

    const normalizedProfessionalSkills = professionalSkills.map(skill => ({
      id: skill.id || Date.now().toString() + Math.random(),
      category: "professionalSkills",
      name: skill.name,
      level: skill.level
    }));

    // Use normalized versions
    formData.append("technicalSkills", JSON.stringify(normalizedTechnicalSkills));
    formData.append("expertiseAreas", JSON.stringify(normalizedExpertiseAreas));
    formData.append("professionalSkills", JSON.stringify(normalizedProfessionalSkills));


    // Append the rest of the sections
    formData.append("experience", JSON.stringify(experienceForm.getValues("experience") || []));
    formData.append("education", JSON.stringify(educationForm.getValues("education") || []));
    
    formData.append("projects", JSON.stringify(projectsForm.getValues("projects") || []));
    formData.append("awards", JSON.stringify(awardsForm.getValues("awards") || []));

    //console.log("formData", formData);

    if (isCreatingNew) {
      serverResponse = await axios.post("/api/add/bio", formData);
      //console.log("serverResponse", serverResponse);
      showAlert("success", serverResponse?.data?.message || "Profile Bio created successfully");
    } else {
      const obfuscatedId = btoa(String(selectedProfile.id)); 
      serverResponse = await axios.put(`/api/edt/profiles/${obfuscatedId}`, formData);
      showAlert("success", serverResponse?.data?.message || "Profile updated successfully");
    }
  
    // Refresh the profiles list
    const response = await axios.get("/api//bio/profiles");
    setProfiles(response.data);
    setIsCreatingNew(false);
  
  } catch (error) {
    // Check if server provided a message
    const errorMessage = error.response?.data?.message || "An unexpected error occurred while saving the profile";
    showAlert("error", errorMessage);
  } finally {
    setIsLoading(false);
  }
};



  const addExperience = () => {
    experienceForm.setValue("experience", [
      ...experienceForm.getValues("experience") || [],
      {
        id: Date.now().toString(),
        company: "",
        position: "",
        period: "",
        description: "",
        achievements: [""]
      }
    ]);
  };

  const removeExperience = (index) => {
    const experiences = experienceForm.getValues("experience");
    experiences.splice(index, 1);
    experienceForm.setValue("experience", experiences);
  };

  const addAchievement = (expIndex) => {
    const achievements = experienceForm.getValues(`experience.${expIndex}.achievements`) || [];
    experienceForm.setValue(`experience.${expIndex}.achievements`, [...achievements, ""]);
  };

  const removeAchievement = (expIndex, achIndex) => {
    const achievements = experienceForm.getValues(`experience.${expIndex}.achievements`);
    achievements.splice(achIndex, 1);
    experienceForm.setValue(`experience.${expIndex}.achievements`, achievements);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Alert Notification */}
      {alert.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-md shadow-lg flex items-center gap-2 ${
          alert.type === "error" ? "bg-red-900/80" : "bg-green-900/80"
        }`}>
          {alert.type === "error" ? (
            <AlertCircle className="h-5 w-5 text-red-300" />
          ) : (
            <CheckCircle className="h-5 w-5 text-green-300" />
          )}
          <span>{alert.message}</span>
        </div>
      )}

      {/* Header */}
      <header className="bg-gray-800 shadow-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-xl font-bold">Profile Content Management</h1>
          <button 
            onClick={handleCreateNew}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Create New Profile
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Profiles List */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-gray-800 rounded-lg shadow border border-gray-700 p-4">
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search profiles..."
                  className="block w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>

              <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                {isLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                  </div>
                ) : filteredProfiles.length === 0 ? (
                  <div className="text-center py-4 text-gray-400">
                    {searchTerm ? "No matching profiles found" : "No profiles available"}
                  </div>
                ) : (
                  filteredProfiles.map(profile => (
                    
                    <div
                      key={profile.id}
                      onClick={() => setSelectedProfile(profile)}
                      className={`p-3 rounded-md cursor-pointer transition-colors ${
                        selectedProfile?.id === profile.id
                          ? "bg-indigo-900/50 border border-indigo-700"
                          : "bg-gray-700/50 hover:bg-gray-700"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <img
                          src={profile.profile?.avatar || "/default-avatar.png"}
                          alt="avatar"
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-medium">{profile.profile?.name || "Unnamed Profile"}</h3>
                          <p className="text-sm text-gray-400 truncate">
                            {profile.profile?.title || "No title"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {validationModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-white mb-4">Missing Information</h3>
              <p className="text-gray-300 mb-6">{validationModal.message}</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    validationModal.onCancel();
                    setValidationModal({ ...validationModal, open: false });
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    validationModal.onConfirm();
                    setValidationModal({ ...validationModal, open: false });
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        )}

          {/* Form Content */}
          <div className="flex-1">
            {selectedProfile || isCreatingNew ? (
              <div className="bg-gray-800 rounded-lg shadow border border-gray-700 overflow-hidden">
                {/* Profile Header */}
                <div className="border-b border-gray-700 p-4 bg-gray-800/50">
                  <h2 className="text-lg font-medium flex items-center gap-2">
                    {isCreatingNew ? (
                      <>
                        <Plus size={18} />
                        Creating New Profile
                      </>
                    ) : (
                      <>
                        <Edit size={18} />
                        Editing: {profileForm.watch("name") || "Unnamed Profile"}
                      </>
                    )}
                  </h2>
                </div>

                {/* Navigation Tabs */}
                <div className="border-b border-gray-700 px-4">
                  <nav className="flex overflow-x-auto scrollbar-hide">
                    {[
                      { id: "profile", icon: BookOpen, label: "Profile" },
                      { id: "experience", icon: Briefcase, label: "Experience" },
                      { id: "education", icon: GraduationCap, label: "Education" },
                      { id: "skills", icon: Layers, label: "Skills" },
                      { id: "projects", icon: Code, label: "Projects" },
                      { id: "awards", icon: Award, label: "Awards" }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveSection(tab.id)}
                        className={`px-4 py-3 font-medium text-sm flex items-center gap-2 whitespace-nowrap ${
                          activeSection === tab.id
                            ? "border-b-2 border-indigo-500 text-indigo-400"
                            : "text-gray-400 hover:text-gray-200"
                        }`}
                      >
                        <tab.icon size={16} />
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Form Sections */}
                <div className="p-6">
                  {/* Profile Info Section */}
                  {activeSection === "profile" && (
                    <form onSubmit={profileForm.handleSubmit(handleSaveProfile)}>
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>                            
                             <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                              <input
                                type="email"
                                {...profileForm.register("email", { required: "Name is required" })}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                            {profileForm.formState.errors.email && (
                              <p className="mt-1 text-sm text-red-400">
                                {profileForm.formState.errors.email.message}
                              </p>
                            )}
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Professional Title*</label>
                            <input
                              type="text"
                              {...profileForm.register("title", { required: "Title is required" })}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            {profileForm.formState.errors.title && (
                              <p className="mt-1 text-sm text-red-400">
                                {profileForm.formState.errors.title.message}
                              </p>
                            )}
                          </div>
                          
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Bio*</label>
                            <textarea
                              rows={4}
                              {...profileForm.register("bio", { required: "Bio is required" })}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            {profileForm.formState.errors.bio && (
                              <p className="mt-1 text-sm text-red-400">
                                {profileForm.formState.errors.bio.message}
                              </p>
                            )}
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Profile Picture</label>
                            
                            {/* Display the existing avatar if available */}
                            {profileForm.avatar && ( 
                              <div className="mb-4">
                                <img
                                  src={profileForm.avatar}
                                  alt="Avatar"
                                  className="w-32 h-32 object-cover rounded-full border border-gray-600"
                                />
                              </div>
                            )}

                            <input
                              type="file"
                              accept="image/*" // Restrict file types to images only
                              onChange={(e) => handleFileChange(e)}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>

                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Location</label>
                            <input
                              type="text"
                              {...profileForm.register("location")}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Birthday</label>
                            <input
                              type="date"
                              {...profileForm.register("birthday")}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>                         
                         
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Website</label>
                            <input
                              type="url"
                              {...profileForm.register("website")}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>
                          
                          <div className="md:col-span-2">
                            <h3 className="text-sm font-medium text-gray-300 mb-3">Social Media</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1">Twitter</label>
                                <input
                                  type="text"
                                  {...profileForm.register("social.twitter")}
                                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1">GitHub</label>
                                <input
                                  type="text"
                                  {...profileForm.register("social.github")}
                                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1">LinkedIn</label>
                                <input
                                  type="text"
                                  {...profileForm.register("social.linkedin")}
                                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="md:col-span-2">
                            <h3 className="text-sm font-medium text-gray-300 mb-3">Statistics</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              {[0, 1, 2, 3].map((index) => (
                                <div key={index}>
                                  <div className="flex gap-2">
                                    <div className="flex-1">
                                      <label className="block text-xs font-medium text-gray-400 mb-1">Value</label>
                                      <input
                                        type="text"
                                        {...profileForm.register(`stats.${index}.value`)}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <label className="block text-xs font-medium text-gray-400 mb-1">Label</label>
                                      <input
                                        type="text"
                                        {...profileForm.register(`stats.${index}.label`)}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-end pt-4 border-t border-gray-700">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            ) : (
                              <Save size={16} />
                            )}
                            Save Profile
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Experience Section */}
                  {activeSection === "experience" && (
                    <form onSubmit={experienceForm.handleSubmit(handleSaveProfile)}>
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            <Briefcase size={18} />
                            Work Experience
                          </h3>
                          <button
                            type="button"
                            onClick={addExperience}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Experience
                          </button>
                        </div>
                        
                        {experienceForm.watch("experience")?.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            No experience entries yet. Add your first work experience.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {experienceForm.watch("experience")?.map((exp, index) => (
                              <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800/50">
                                <div className="flex justify-between items-start mb-4">
                                  <h4 className="font-medium">Experience #{index + 1}</h4>
                                  <button
                                    type="button"
                                    onClick={() => removeExperience(index)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Company*</label>
                                    <input
                                      type="text"
                                      {...experienceForm.register(`experience.${index}.company`, {
                                        required: "Company name is required"
                                      })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {experienceForm.formState.errors.experience?.[index]?.company && (
                                      <p className="mt-1 text-sm text-red-400">
                                        {experienceForm.formState.errors.experience[index].company.message}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Position*</label>
                                    <input
                                      type="text"
                                      {...experienceForm.register(`experience.${index}.position`, {
                                        required: "Position is required"
                                      })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {experienceForm.formState.errors.experience?.[index]?.position && (
                                      <p className="mt-1 text-sm text-red-400">
                                        {experienceForm.formState.errors.experience[index].position.message}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Period*</label>
                                    <input
                                      type="text"
                                      {...experienceForm.register(`experience.${index}.period`, {
                                        required: "Period is required"
                                      })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {experienceForm.formState.errors.experience?.[index]?.period && (
                                      <p className="mt-1 text-sm text-red-400">
                                        {experienceForm.formState.errors.experience[index].period.message}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="mb-4">
                                  <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                  <textarea
                                    rows={3}
                                    {...experienceForm.register(`experience.${index}.description`)}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-300 mb-1">Achievements</label>
                                  <div className="space-y-2">
                                    {experienceForm.watch(`experience.${index}.achievements`)?.map((_, aIndex) => (
                                      <div key={aIndex} className="flex gap-2">
                                        <input
                                          type="text"
                                          {...experienceForm.register(`experience.${index}.achievements.${aIndex}`)}
                                          className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => removeAchievement(index, aIndex)}
                                          className="px-2 text-red-400 hover:text-red-300"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => addAchievement(index)}
                                    className="mt-2 text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                                  >
                                    <Plus size={14} />
                                    Add Achievement
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex justify-end pt-4 border-t border-gray-700">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            ) : (
                              <Save size={16} />
                            )}
                            Save Experience
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Education Section */}
                  {activeSection === "education" && (
                    <form onSubmit={educationForm.handleSubmit(handleSaveProfile)}>
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            <GraduationCap size={18} />
                            Education
                          </h3>
                          <button
                            type="button"
                            onClick={() => educationForm.setValue("education", [
                              ...educationForm.getValues("education") || [],
                              {
                                id: Date.now().toString(),
                                institution: "",
                                degree: "",
                                year: "",
                                thesis: ""
                              }
                            ])}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Education
                          </button>
                        </div>
                        
                        {educationForm.watch("education")?.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            No education entries yet. Add your first education.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {educationForm.watch("education")?.map((edu, index) => (
                              <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800/50">
                                <div className="flex justify-between items-start mb-4">
                                  <h4 className="font-medium">Education #{index + 1}</h4>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const education = educationForm.getValues("education");
                                      education.splice(index, 1);
                                      educationForm.setValue("education", education);
                                    }}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Institution*</label>
                                    <input
                                      type="text"
                                      {...educationForm.register(`education.${index}.institution`, {
                                        required: "Institution is required"
                                      })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {educationForm.formState.errors.education?.[index]?.institution && (
                                      <p className="mt-1 text-sm text-red-400">
                                        {educationForm.formState.errors.education[index].institution.message}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Degree*</label>
                                    <input
                                      type="text"
                                      {...educationForm.register(`education.${index}.degree`, {
                                        required: "Degree is required"
                                      })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {educationForm.formState.errors.education?.[index]?.degree && (
                                      <p className="mt-1 text-sm text-red-400">
                                        {educationForm.formState.errors.education[index].degree.message}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Year</label>
                                    <input
                                      type="text"
                                      {...educationForm.register(`education.${index}.year`)}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-300 mb-1">Thesis/Dissertation</label>
                                  <input
                                    type="text"
                                    {...educationForm.register(`education.${index}.thesis`)}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex justify-end pt-4 border-t border-gray-700">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            ) : (
                              <Save size={16} />
                            )}
                            Save Education
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Skills Section */}
                  {activeSection === "skills" && (
                    <form onSubmit={skillsForm.handleSubmit(handleSaveProfile)}>
                      <div className="space-y-6">
                        
                        {/* Technical Skills Section */}
                        <div>
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            Technical Skills
                          </h3>
                          <button
                            type="button"
                            onClick={() => skillsForm.setValue("technicalSkills", [
                              ...skillsForm.getValues("technicalSkills") || [],
                              { category: "technicalSkills", level: 0, name: "" } // Updated structure
                            ])}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Technical Skill
                          </button>

                          {skillsForm.watch("technicalSkills")?.length === 0 ? (
                            <div className="text-center py-4 text-gray-400">
                              No technical skills entries yet. Add your first skill.
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {skillsForm.watch("technicalSkills")?.map((skill, index) => (
                                <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800/50">
                                  <div className="flex justify-between items-start mb-4">
                                    <h4 className="font-medium">{skill.name || "Technical Skill"}</h4>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const skills = skillsForm.getValues("technicalSkills");
                                        skills.splice(index, 1);
                                        skillsForm.setValue("technicalSkills", skills);
                                      }}
                                      className="text-red-400 hover:text-red-300"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="md:col-span-3">
                                      <label className="block text-sm font-medium text-gray-300 mb-1">Skill Name*</label>
                                      <input
                                        type="text"
                                        placeholder="Enter skill name"
                                        {...skillsForm.register(`technicalSkills.${index}.name`, {
                                          required: "Skill name is required"
                                        })}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      />
                                      {skillsForm.formState.errors.technicalSkills?.[index]?.name && (
                                        <p className="mt-1 text-sm text-red-400">
                                          {skillsForm.formState.errors.technicalSkills[index].name.message}
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className="md:col-span-1">
                                      <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Proficiency Level: {skillsForm.watch(`technicalSkills.${index}.level`) || 0}%
                                      </label>
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        {...skillsForm.register(`technicalSkills.${index}.level`, { valueAsNumber: true })}
                                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Expertise Areas Section */}
                        <div>
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            Expertise Areas
                          </h3>
                          <button
                            type="button"
                            onClick={() => skillsForm.setValue("expertiseAreas", [
                              ...skillsForm.getValues("expertiseAreas") || [],
                              { id: Date.now().toString(), name: "", level: 0 } // Initialize new area with empty name
                            ])}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Expertise Area
                          </button>

                          {skillsForm.watch("expertiseAreas")?.length === 0 ? (
                            <div className="text-center py-4 text-gray-400">
                              No expertise areas entries yet. Add your first area.
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {skillsForm.watch("expertiseAreas")?.map((area, index) => (
                                <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800/50">
                                  <div className="flex justify-between items-start mb-4">
                                    <h4 className="font-medium">{area.name || "Expertise Area"}</h4>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const areas = skillsForm.getValues("expertiseAreas");
                                        areas.splice(index, 1);
                                        skillsForm.setValue("expertiseAreas", areas);
                                      }}
                                      className="text-red-400 hover:text-red-300"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="md:col-span-3">
                                      <label className="block text-sm font-medium text-gray-300 mb-1">Skill Name*</label>
                                      <input
                                        type="text"
                                        placeholder="Enter area name"
                                        {...skillsForm.register(`expertiseAreas.${index}.name`, {
                                          required: "Area name is required"
                                        })}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      />
                                      {skillsForm.formState.errors.expertiseAreas?.[index]?.name && (
                                        <p className="mt-1 text-sm text-red-400">
                                          {skillsForm.formState.errors.expertiseAreas[index].name.message}
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className="md:col-span-1">
                                      <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Proficiency Level: {skillsForm.watch(`expertiseAreas.${index}.level`) || 0}%
                                      </label>
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        {...skillsForm.register(`expertiseAreas.${index}.level`, { valueAsNumber: true })}
                                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Professional Skills Section */}
                        <div>
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            Professional Skills
                          </h3>
                          <button
                            type="button"
                            onClick={() => skillsForm.setValue("professionalSkills", [
                              ...skillsForm.getValues("professionalSkills") || [],
                              { category: "professionalSkills", level: 0, name: "" } // Updated structure
                            ])}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Professional Skill
                          </button>

                          {skillsForm.watch("professionalSkills")?.length === 0 ? (
                            <div className="text-center py-4 text-gray-400">
                              No professional skills entries yet. Add your first skill.
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {skillsForm.watch("professionalSkills")?.map((skill, index) => (
                                <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800/50">
                                  <div className="flex justify-between items-start mb-4">
                                    <h4 className="font-medium">{skill.name || "Professional Skill"}</h4>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const skills = skillsForm.getValues("professionalSkills");
                                        skills.splice(index, 1);
                                        skillsForm.setValue("professionalSkills", skills);
                                      }}
                                      className="text-red-400 hover:text-red-300"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="md:col-span-3">
                                      <label className="block text-sm font-medium text-gray-300 mb-1">Skill Name*</label>
                                      <input
                                        type="text"
                                        placeholder="Enter skill name"
                                        {...skillsForm.register(`professionalSkills.${index}.name`, {
                                          required: "Skill name is required"
                                        })}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      />
                                      {skillsForm.formState.errors.professionalSkills?.[index]?.name && (
                                        <p className="mt-1 text-sm text-red-400">
                                          {skillsForm.formState.errors.professionalSkills[index].name.message}
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className="md:col-span-1">
                                      <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Proficiency Level: {skillsForm.watch(`professionalSkills.${index}.level`) || 0}%
                                      </label>
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        {...skillsForm.register(`professionalSkills.${index}.level`, { valueAsNumber: true })}
                                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end pt-4 border-t border-gray-700">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            ) : (
                              <Save size={16} />
                            )}
                            Save Skills
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Projects Section */}
                  {activeSection === "projects" && (
                    <form onSubmit={projectsForm.handleSubmit(handleSaveProfile)}>
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            <Code size={18} />
                            Projects
                          </h3>
                          <button
                            type="button"
                            onClick={() => projectsForm.setValue("projects", [
                              ...projectsForm.getValues("projects") || [],
                              {
                                id: Date.now().toString(),
                                name: "",
                                description: "",
                                technologies: [],
                                link: "",
                                stars: 0
                              }
                            ])}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Project
                          </button>
                        </div>
                        
                        {projectsForm.watch("projects")?.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            No projects entries yet. Add your first project.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {projectsForm.watch("projects")?.map((project, index) => (
                              <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800/50">
                                <div className="flex justify-between items-start mb-4">
                                  <h4 className="font-medium">Project #{index + 1}</h4>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const projects = projectsForm.getValues("projects");
                                      projects.splice(index, 1);
                                      projectsForm.setValue("projects", projects);
                                    }}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                                
                                <div className="space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Project Name*</label>
                                    <input
                                      type="text"
                                      {...projectsForm.register(`projects.${index}.name`, {
                                        required: "Project name is required"
                                      })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {projectsForm.formState.errors.projects?.[index]?.name && (
                                      <p className="mt-1 text-sm text-red-400">
                                        {projectsForm.formState.errors.projects[index].name.message}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                    <textarea
                                      rows={3}
                                      {...projectsForm.register(`projects.${index}.description`)}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Technologies (comma separated)</label>
                                    <input
                                      type="text"
                                      defaultValue={projectsForm.watch(`projects.${index}.technologies`)?.join(", ")}
                                      onChange={(e) => {
                                        const techs = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                                        projectsForm.setValue(`projects.${index}.technologies`, techs);
                                      }}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Project URL</label>
                                    <input
                                      type="url"
                                      {...projectsForm.register(`projects.${index}.link`)}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">GitHub Stars</label>
                                    <input
                                      type="number"
                                      {...projectsForm.register(`projects.${index}.stars`, { valueAsNumber: true })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex justify-end pt-4 border-t border-gray-700">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            ) : (
                              <Save size={16} />
                            )}
                            Save Projects
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Awards Section */}
                  {activeSection === "awards" && (
                    <form onSubmit={awardsForm.handleSubmit(handleSaveProfile)}>
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-medium flex items-center gap-2">
                            <Award size={18} />
                            Awards & Recognition
                          </h3>
                          <button
                            type="button"
                            onClick={() => awardsForm.setValue("awards", [
                              ...awardsForm.getValues("awards") || [],
                              {
                                id: Date.now().toString(),
                                name: "",
                                issuer: "",
                                year: "",
                                description: ""
                              }
                            ])}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Award
                          </button>
                        </div>
                        
                        {awardsForm.watch("awards")?.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            No awards entries yet. Add your first award.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {awardsForm.watch("awards")?.map((award, index) => (
                              <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800/50">
                                <div className="flex justify-between items-start mb-4">
                                  <h4 className="font-medium">Award #{index + 1}</h4>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const awards = awardsForm.getValues("awards");
                                      awards.splice(index, 1);
                                      awardsForm.setValue("awards", awards);
                                    }}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Award Name*</label>
                                    <input
                                      type="text"
                                      {...awardsForm.register(`awards.${index}.name`, {
                                        required: "Award name is required"
                                      })}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {awardsForm.formState.errors.awards?.[index]?.name && (
                                      <p className="mt-1 text-sm text-red-400">
                                        {awardsForm.formState.errors.awards[index].name.message}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Issuer</label>
                                    <input
                                      type="text"
                                      {...awardsForm.register(`awards.${index}.issuer`)}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Year</label>
                                    <input
                                      type="text"
                                      {...awardsForm.register(`awards.${index}.year`)}
                                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                  <textarea
                                    rows={3}
                                    {...awardsForm.register(`awards.${index}.description`)}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex justify-end pt-4 border-t border-gray-700">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            ) : (
                              <Save size={16} />
                            )}
                            Save Awards
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg shadow border border-gray-700 p-8 text-center">
                <div className="max-w-md mx-auto">
                  <BookOpen className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-300 mb-2">No Profile Selected</h3>
                  <p className="text-gray-500 mb-6">
                    Select a profile from the list to edit, or create a new profile to get started.
                  </p>
                  <button
                    onClick={handleCreateNew}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 mx-auto"
                  >
                    <Plus size={16} />
                    Create New Profile
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}